/**
 * Utility function to check whether is a mobile device or not
 *
 * @returns {boolean} true if it is a mobile device
 */
window.isMobile = function isMobile() {
  const mobileAgentHash = [
    "mobile",
    "tablet",
    "phone",
    "ipad",
    "ipod",
    "android",
    "blackberry",
    "windows ce",
    "opera mini",
    "palm",
  ];
  let idx = 0;
  let isMobile = false;
  const userAgent = navigator.userAgent.toLowerCase();

  while (mobileAgentHash[idx] && !isMobile) {
    isMobile = userAgent.indexOf(mobileAgentHash[idx]) >= 0;
    idx++;
  }
  return isMobile;
};

/**
 * Uses isMobile function to return if the device is Mobile/Desktop
 *
 * @returns {"mobile" | "desktop"} return mobile/desktop string regarding the device
 */
window.findDeviceType = () => (window.isMobile() ? "mobile" : "desktop");

/**
 * Recursively queries nested elements and shadow roots by traversing through multiple
 * layers of shadow DOMs by applying each selector in order. At each step, if the found element has
 * a shadow root, the search continues inside it.
 *
 * @param {ParentNode} root - Starting element
 * @param {string[]} selectors - Array of CSS selectors to query in current context
 * @returns {Element | null} - Final matched element if all selectors resolved or null if none matched.
 */
window.deepQuerySelector = function deepQuerySelector(root, selectors) {
  let el = root;
  for (const s of selectors) {
    el = el?.querySelector(s);
    if (!el) return null;
    if (el.shadowRoot) el = el.shadowRoot;
  }
  return el;
};

// ============================================================================
// MPARTICLE EVENT PUBLISHER - Standardized event publishing
// ============================================================================

/**
 * Standardized mParticle event publisher
 * @param {Object} config - Event configuration
 * @param {string} config.eventName - Name of the event
 * @param {string} [config.eventCategory] - Category of the event
 * @param {string} [config.eventType] - Type of event (default: "Other")
 * @param {Object} [config.payload] - Event payload data
 */
function publishMParticleEvent({
  eventName,
  eventCategory,
  eventType = "Other",
  payload = {},
  options,
}) {
  if (!window.Shopify?.analytics) {
    console.warn("[mParticle] Shopify analytics not available");
    return;
  }

  const data = {
    eventName,
    eventType,
    payload,
    options: options || {},
  };

  if (eventCategory) {
    data.eventCategory = eventCategory;
  }

  window.Shopify.analytics.publish("mp:custom-event", data);
}

// ============================================================================
// KLAVIYO EVENTS
// ============================================================================

function setupKlaviyoEvents() {
  try {
    // Lightbox modal form submission - using click event delegation
    document.addEventListener("click", function (e) {
      const target = e.target;
      const klaviyoForm =
        target.matches("#SubscribeForm button") ||
        target.matches(".klaviyo-form-version-cid_1 button");

      if (!klaviyoForm) return;

      const form = target.closest("form");
      const email = form?.querySelector('input[name="email"]')?.value;
      if (!email) return;

      if (window.Shopify) {
        window.Shopify.analytics.publish("mp:klaviyo", {
          email,
          source: "Klaviyo lightbox",
          content: "newsletter",
        });
      }
    });

    // Footer form submission
    document
      .querySelector('form[id^="newsletter-form-sections--"][id$="__footer"]')
      ?.addEventListener("submit", function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const form = event.target;

        if (!(form instanceof HTMLFormElement)) return;

        const isNewsletterForm =
          (form.id && form.id.toString().startsWith("newsletter-form")) ||
          form.classList.contains("klaviyo-form") ||
          (form.dataset.testid && form.dataset.testid.includes("klaviyo-form"));

        if (!isNewsletterForm) return;

        const emailInput = form.querySelector(
          'input[type="email"], input[name*="email"]'
        );
        if (!emailInput) return;

        const email = emailInput.value.trim();
        if (!email) return;

        if (window.Shopify?.analytics) {
          window.Shopify.analytics.publish("mp:klaviyo", {
            email,
            source: "Footer",
            content: "newsletter",
          });
        }

        // Submit button to loading state
        const button = form.querySelector("button[type='submit']");
        if (button) {
          button.setAttribute("aria-busy", "true");
          button.disabled = true;
        }

        setTimeout(() => {
          form.submit();
        }, 1000);
      });
  } catch (error) {
    console.warn("[mParticle] Error setting up Klaviyo events:", error);
  }
}

// ============================================================================
// PRODUCT CLICK EVENTS
// ============================================================================

function setupProductClickEvents() {
  try {
    document.addEventListener("click", function(e) {
      const productCard = e.target.closest("product-card, .product-card, [data-product-card]");
      if (!productCard) return;
      
      const link = e.target.closest("a");
      if (!link) return;
      
      // Extract product data from data attributes
      const productTitle = productCard.dataset.productTitle;
      const sku = productCard.dataset.sku;
      const price = productCard.dataset.price;
      const type = productCard.dataset.type;
      const vendor = productCard.dataset.vendor;
      
      if (!productTitle || !sku) return;
      
      // Fire native Shopify product_clicked event
      if (window.Shopify?.analytics) {
        window.Shopify.analytics.publish("product_clicked", {
          productVariant: {
            product: {
              title: productTitle,
              type: type,
              vendor: vendor
            },
            sku: sku,
            price: { 
              amount: parseFloat(price) / 100, 
              currencyCode: "USD" 
            }
          }
        });
      }
    });
  } catch (error) {
    console.warn("[mParticle] Error setting up Product Click events:", error);
  }
}

// ============================================================================
// PIE EYE CONSENT UPDATE EVENTS
// ============================================================================

function setupPieEyeEvents() {
  function getConsentOptOutRegion(activeConsents) {
    var pieEyeOptOutConsetRegion = "";
    if (window.localStorage) {
      var pieEyeRegionData = "",
        pieEyeConsentString = "";
      pieEyeRegionData = window.localStorage.getItem("ipDetailsData");
      pieEyeConsentString = activeConsents;
      try {
        var pieEyeTargettingConsentStatus = false;
        if (pieEyeRegionData && pieEyeRegionData.length > 0) {
          pieEyeRegionData = JSON.parse(pieEyeRegionData);
          if (pieEyeConsentString.length > 0) {
            pieEyeTargettingConsentStatus =
              pieEyeConsentString.indexOf("Targeting/Advertising") > -1
                ? false
                : true;
          }
          if (pieEyeTargettingConsentStatus) {
            if (pieEyeRegionData) {
              pieEyeOptOutConsetRegion =
                pieEyeRegionData.region && pieEyeRegionData.region.code
                  ? pieEyeRegionData.region.code
                  : "";
            }
          }
        }
      } catch (e) {}
    }
    return pieEyeOptOutConsetRegion;
  }

  window.onCookieBannerLoad = async function onCookieBannerLoad(input) {
    setTimeout(function () {
      var piiConsents = window.localStorage.getItem(
        "pieeye-categories-consent"
      );
      input.cookies = piiConsents;

      var activeConsents = input.cookies?.split(",") || [];
      var targetingConsentOptOutRegion = getConsentOptOutRegion(activeConsents);
      input.targetingConsentOptOutRegion = targetingConsentOptOutRegion;

      if (window.Shopify?.analytics) {
        window.Shopify.analytics.publish("mp:pii-consent-update", input);
      }
    }, 3000);
  };

  window.onCookieConsent = async function onCookieConsent(input) {
    var activeConsents = input.cookies?.split(",") || [];
    var targetingConsentOptOutRegion = getConsentOptOutRegion(activeConsents);
    input.targetingConsentOptOutRegion = targetingConsentOptOutRegion;

    if (window.Shopify?.analytics) {
      window.Shopify.analytics.publish("mp:pii-consent-update", input);
    }
  };
}

// ============================================================================
// HUE INTEGRATION EVENTS
// 1. Hue ATB Event
// ============================================================================

function setupHueEvents() {
  try {
    function onHueATB(e) {
      const detail = e.detail;
      if (!detail) return;

      let slotNumber, creator;

      if (detail && detail.checkoutEvent && detail.checkoutEvent.reviewOrder) {
        const index = detail.checkoutEvent.reviewOrder - 1;
        slotNumber = detail.checkoutEvent.reviewOrder;

        const slideClass = ".hue-video-module-slider-slide";
        const slideElement = document.querySelectorAll(slideClass)[index];
        if (slideElement) {
          creator = slideElement.querySelector(
            ".hue-slide-review__name"
          )?.textContent;
        }
      } else {
        slotNumber = "Global ATB";
      }

      const eventPayload = {
        deviceType: findDeviceType(),
        pagePath: window.location.pathname,
        slotNumber,
      };

      if (creator) eventPayload.creator = creator;

      publishMParticleEvent({
        eventName: "Hue module ATB",
        eventType: "Other",
        payload: eventPayload,
        options: { removeCustomAttributes: true },
      });
    }

    document.addEventListener("hue-add-to-cart", onHueATB);
  } catch (error) {
    console.warn("[mParticle] Error setting up Hue events:", error);
  }
}

// ============================================================================
// MINI BAG EVENTS (Gift Wrap & Samples)
// ============================================================================

function setupMiniBagEvents() {
  try {
    // Element finders using shadow DOM traversal
    const findSaveButton = () =>
      window.deepQuerySelector(document, [
        "wrapped-gift-block",
        "#save-button",
      ]);

    const findGiftMessageTextArea = () =>
      window.deepQuerySelector(document, [
        "wrapped-gift-block",
        "gift-dialog-message textarea",
      ]);

    const findGiftMessageMode = () =>
      window.deepQuerySelector(document, [
        "wrapped-gift-block",
        "shopify-variant-selector",
        "select[name='option-Title']",
      ]);

    const findGiftWrapButton = () =>
      window.deepQuerySelector(document, [
        "wrapped-gift-block",
        "shopify-context > gift-block-launcher",
        "button.gift-block__launcher",
      ]);

    // Gift Wrap Modal Open Event
    function onGiftWrapModalOpen() {
      publishMParticleEvent({
        eventName: "Gift Options Modal Click",
        eventCategory: "Gift Options",
        payload: {},
      });
    }

    // Gift Wrap Message Save Event
    function onGiftWrapMsgSave() {
      const giftMessage = findGiftMessageTextArea()?.value || "";
      const giftMode = findGiftMessageMode()?.value?.trim();
      let giftwrapSelected;

      if (giftMode === "Gift Wrap & Message") {
        giftwrapSelected = true;
      } else if (giftMode === "Only Message") {
        giftwrapSelected = false;
      }

      publishMParticleEvent({
        eventName: "Gift Options Selected",
        eventCategory: "Checkout",
        payload: { giftMessage, giftwrapSelected },
      });
    }

    /**
     * Attaches 'click' event listener to the Gift Wrap button and Save button.
     * The listener is attached only once using dataset.listenerAttached flag.
     */
    function attachGiftWrapListeners() {
      const giftWrapButton = findGiftWrapButton();
      const saveBtn = findSaveButton();

      if (giftWrapButton && !giftWrapButton.dataset.listenerAttached) {
        giftWrapButton.addEventListener("click", onGiftWrapModalOpen);
        giftWrapButton.dataset.listenerAttached = "true";
      }

      if (saveBtn && !saveBtn.dataset.listenerAttached) {
        saveBtn.addEventListener("click", onGiftWrapMsgSave);
        saveBtn.dataset.listenerAttached = "true";
      }
    }

    /**
     * Attaches 'click' event listener to the Sample Accordion.
     * The listener is attached only once using dataset.listenerAttached flag.
     * Handles both accordion header click and sample add/remove actions.
     */
    function attachSamplesListeners() {
      const sampleAccordion = document
        .querySelector("span[data-sample-product-counter='selected']")
        ?.closest(".accordion__disclosure");

      if (!sampleAccordion || sampleAccordion.dataset.listenerAttached) {
        return;
      }

      sampleAccordion.addEventListener("click", function (e) {
        // Delay to allow DOM to update after click
        setTimeout(() => {
          // Handle accordion header click event
          const isSampleAddClicked = e.target.closest(".accordion__summary");
          if (isSampleAddClicked && window.Shopify) {
            try {
              const isOpened = sampleAccordion?.hasAttribute("open");
              if (isOpened) {
                publishMParticleEvent({
                  eventName: "Sample Action",
                  eventCategory: "Samples",
                  payload: {
                    actionType: "Click Add Sample",
                    deviceType: window.findDeviceType(),
                    pageSource: "Mini-bag",
                  },
                });
              }
            } catch (err) {
              console.warn("[mParticle] Error in sample header click:", err);
            }
          }

          // Handle sample add/remove button click
          const sampleClicked = e.target.closest(
            "button[data-sample-product-card='freesample']"
          );
          if (sampleClicked && window.Shopify) {
            try {
              const variantId =
                sampleClicked.getAttribute("data-id") ||
                sampleClicked.getAttribute("data-line-item-id");
              const isAdded =
                sampleClicked.getAttribute("data-selected") === "false";

              publishMParticleEvent({
                eventName: "Sample Action",
                eventCategory: "Samples",
                payload: {
                  actionType: isAdded ? "Add Sample" : "Remove Sample",
                  deviceType: window.findDeviceType(),
                  pageSource: "Mini-bag",
                  productID: variantId,
                },
              });
            } catch (err) {
              console.warn("[mParticle] Error in sample add/remove:", err);
            }
          }
        }, 300);
      });

      sampleAccordion.dataset.listenerAttached = "true";
    }

    /**
     * Reattaches all mini bag event listeners.
     * Called repeatedly via polling to handle DOM recreation by third-party components.
     */
    function attachAllListeners() {
      attachGiftWrapListeners();
      attachSamplesListeners();
    }

    // Polling approach: Required because third-party components completely recreate DOM
    let pollingInterval = null;
    const cartDrawer = document.querySelector("cart-drawer");

    if (cartDrawer) {
      cartDrawer.addEventListener("dialog:before-show", () => {
        if (pollingInterval) clearInterval(pollingInterval);
        // Poll every second to reattach listeners if DOM was recreated
        pollingInterval = setInterval(attachAllListeners, 1000);
      });

      cartDrawer.addEventListener("dialog:before-hide", () => {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = null;
        }
      });
    }
  } catch (error) {
    console.warn("[mParticle] Error setting up Mini Bag events:", error);
  }
}

// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

function setupNavigationEvents() {
  try {
    const sendLoginClickEvent = () =>
      publishMParticleEvent({
        eventName: "Login Click",
        eventType: "Other",
        payload: {
          linkText: "Login",
        },
        options: {
          saveForPageChange: true,
        },
      });

    /**
     * Handles navigation link clicks and sends mParticle events
     */
    function sendNavEvents(event) {
      const link = event.target.closest("a, button");
      if (!link) return;

      // Skip if explicitly marked as no-track
      if (link.hasAttribute("data-mParticle-no-track")) return;

      const assignedClickType = link.getAttribute("data-mParticle");

      // Other cases
      if (assignedClickType === "Login Click") {
        sendLoginClickEvent();
        return;
      }

      const allowedClickTypes = [
        "Top Level Nav",
        "Main Link",
        "Bag Click",
        "search-form-list",
      ];

      if (!allowedClickTypes.includes(assignedClickType)) return;

      const linkText = link.textContent.replace(/\s+/g, " ").trim();

      const deviceType = window.findDeviceType();

      publishMParticleEvent({
        eventName: "Navigation Click",
        eventCategory: `${
          deviceType === "mobile" ? "Mobile" : "Desktop"
        } Navigation`,
        eventType: "Navigation",
        payload: {
          linkText,
          clickType: assignedClickType,
          deviceType,
        },
        options: {
          saveForPageChange: true,
        },
      });
    }

    /**
     * Handles Category Mod link clicks (moisturizers, cleansers, etc) to single category pages' mParticle events
     */
    function sendCategoryModEvent(event) {
      const link = event.target.closest("a, button");
      if (!link) return;

      const linkUrl = link.href;
      const linkText = link.textContent.replace(/\s+/g, " ").trim();

      let id = "";
      const imgElement = link.querySelector("img");
      if (imgElement) {
        const url = new URL(imgElement.src);
        id = `${url.origin}${url.pathname}`;
      }

      const payload = {
        id,
        linkUrl,
        linkText,
        deviceType: window.findDeviceType(),
      };

      publishMParticleEvent({
        eventName: "Category Mod Click",
        eventType: "Navigation",
        payload,
        options: { removeCustomAttributes: true },
      });
    }

    /**
     * Handles search results ready event and sends mParticle event
     */
    function sendSearchEvent() {
      const resultsCount = document
        .querySelector(".predictive-search__products")
        ?.querySelectorAll("product-card")?.length;

      const searchTerm =
        document.querySelector("input#search-drawer-input")?.value || "";

      if (typeof resultsCount !== "number" || isNaN(resultsCount)) {
        return;
      }

      const deviceType = window.findDeviceType();

      publishMParticleEvent({
        eventName: "Navigation Click",
        eventCategory: `${window.findDeviceType()} Navigation`,
        eventType: "Navigation",
        payload: {
          linkText: "",
          clickType: "search-form-list",
          searchTerm,
          deviceType,
        },
      });
    }

    const header = document.querySelector("x-header");
    if (header) {
      header.addEventListener("click", sendNavEvents);
    }

    const categoryMod = document.querySelector("#category-mod-nav");
    if (categoryMod) {
      categoryMod.addEventListener("click", sendCategoryModEvent);
    }

    document.addEventListener("search:results:ready", sendSearchEvent);
  } catch (error) {
    console.warn("[mParticle] Error setting up Navigation events:", error);
  }
}

// ============================================================================
// FILTER EVENTS
// ============================================================================

function setupFilterSelectedEvents() {
  /**
   * Collects all filter data from the facets form
   * @returns {Object} Object containing all filter selections
   */
  function getAllFilterData() {
    try {
      const sortBy = document.querySelector(
        "facets-form input[name='sort_by']:checked + label"
      )?.textContent;

      const skinTypeInputs = document.querySelectorAll(
        "facets-form input[name='filter.p.m.custom.skintype']:checked"
      );
      const skinType = [...skinTypeInputs].map((input) => input.value);

      const solutionsInputs = document.querySelectorAll(
        "facets-form input[name='filter.p.m.custom.skinconcerns']:checked"
      );
      const solutions = [...solutionsInputs].map((input) => input.value);

      const inStockCheckbox = document.querySelector(
        "facets-form input[name='filter.v.availability']"
      );
      const inStockOnly = inStockCheckbox?.hasAttribute("checked") || false;

      // Build changed values array based on actual selections
      const changedValues = [];
      if (skinType.length > 0) changedValues.push("skin type");
      if (solutions.length > 0) changedValues.push("solutions");
      if (inStockOnly) changedValues.push("in stock only");

      const updatedFilters = {};
      if (skinType.length > 0) {
        updatedFilters["Category filter: skin type"] = [
          ...new Set(skinType),
        ].join(", ");
      }
      if (solutions.length > 0) {
        updatedFilters["Category filter: solutions"] = [
          ...new Set(solutions),
        ].join(", ");
      }
      updatedFilters["Category filter: in stock only"] = inStockOnly;
      if (sortBy) {
        updatedFilters["Category Sort"] = sortBy;
      }
      updatedFilters["filterSelection"] = changedValues.join(", ");

      return updatedFilters;
    } catch (error) {
      console.warn(
        "[mParticle] Error gathering data from filter faucets:",
        error
      );
    }
  }

  document.addEventListener("submit", function (e) {
    const currentForm = e.target;
    const isFacetForm = currentForm.closest("facets-form") !== null;

    // Event delegation used for faucet-form that recreates itself once closed
    if (!isFacetForm) return;

    // Wait for the filter DOM elements to get updated with new values
    setTimeout(() => {
      const payload = getAllFilterData();
      window.Shopify?.analytics.publish("mp:filter-selected", { payload });
    }, 300);
  });
}

// ============================================================================
// DYNAMIC YIELD EVENTS
// ============================================================================

function setupDYEvents() {
  try {
    function exec() {
      if (!window.DYO) {
        return;
      }

      const dyCampaignObj = window.DYO?.getUserObjectsAndVariations();
      if (
        !dyCampaignObj ||
        !Array.isArray(dyCampaignObj) ||
        dyCampaignObj.length === 0
      ) {
        console.warn("No DynamicYield Active Campaigns to send");
        return;
      }

      const dyCampaign = dyCampaignObj.map((campaign) => ({
        campaignName: campaign?.objectName || "",
        variationID: campaign?.variationIds?.[0] || "",
        variationName: campaign?.variations?.[0] || "",
      }));

      if (dyCampaign.length > 0) {
        publishMParticleEvent({
          eventName: "DY Active Campaigns",
          eventCategory: "Dynamic Yield",
          payload: {
            campaigns: JSON.stringify(dyCampaign),
          },
        });
      }
    }

    // Polling is required because DYO loads asynchronously
    // and does not expose a reliable ready callback.
    // window.DY.API("callback") is also not available immediately on DOM load.

    const interval = 300; // ms between checks
    const maxAttempts = 20; // don’t poll forever
    let attempts = 0;

    const poller = setInterval(() => {
      attempts++;

      if (typeof window.DYO?.getUserObjectsAndVariations === "function") {
        clearInterval(poller);
        exec();
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(poller);
      }
    }, interval);
  } catch (error) {
    console.warn("[mParticle] Error setting up DY events:", error);
  }
}

// ============================================================================
// RITUAL FINDER EVENTS
// ============================================================================

function setupRitualFinderEvents() {
  try {
    const ritualFinderKeywords = ["ritual-finder", "ritualfinder-results"];
    const isRitualFinderPage = ritualFinderKeywords.some((keyword) =>
      window.location.pathname?.includes(keyword)
    );

    // Assign Ritual Finder event listeners only if they are in ritual finder pages
    if (!isRitualFinderPage || !window.Shopify) return;

    let lastSentEvent = null;

    function sendTabChangedEvents() {
      const activeTab = document.querySelector("rf-question:not([hidden])");

      const stepMap = {
        skinType: "Skin Type",
        skinConcerns: "Skin Wishes",
        eyeConcerns: "Eye Concerns",
        results: "Current Routine",
      };

      const step = stepMap[activeTab?.dataset.type];

      const isResultStep = step === "Current Routine" && lastSentEvent === step;
      if (!step || isResultStep) return;

      window.Shopify.analytics.publish("mp:rf-steps-navigation", { step });
      lastSentEvent = step;
    }

    function sendValueUpdatedEvents() {
      const activeTab = document.querySelector("rf-question:not([hidden])");
      const activeId = activeTab?.id; // "step2", "step3", etc.

      const stepNumber = Number(activeId?.replace("step", "")) - 1;

      if (isNaN(stepNumber) || stepNumber < 1 || stepNumber > 4) {
        // No need to send event in these scenarios.
        return;
      }

      const isSingleChoice = [2].includes(stepNumber);
      const selector = isSingleChoice
        ? `rf-question#step${stepNumber} .rf-question__input:checked`
        : `rf-question#step${stepNumber} .rf-question__input:not(.disabled)`;
      const selectedInputs = [...document.querySelectorAll(selector)];

      const value = selectedInputs
        .map((input) => input.value?.replaceAll("_", " "))
        .join(", ");

      const attributeMap = {
        0: "Ritual Finder Sensitivity",
        1: "Ritual Finder Skin Type",
        2: "Ritual Finder Skin Wishes",
        3: "Ritual Finder Eye Concerns",
      };

      const attribute = attributeMap[stepNumber.toString()];
      if (!attribute) return;

      if (stepNumber === 1) {
        const query = "rf-question#step1 input#q-sensitive-skin";
        const sensitivityChoice = document.querySelector(query)?.checked;
        if (typeof sensitivityChoice === "boolean")
          window.Shopify.analytics.publish("mp:rf-attribute-update", {
            attribute: attributeMap["0"],
            value: sensitivityChoice,
          });
      }

      window.Shopify.analytics.publish("mp:rf-attribute-update", {
        attribute,
        value,
      });
    }

    // Send mParticle Navigation event if tabs are changed through footer buttons.
    document.addEventListener("rf-footer-next", function () {
      setTimeout(() => {
        sendTabChangedEvents();
        sendValueUpdatedEvents();
      }, 300);
    });

    document.addEventListener("rf-footer-back", function () {
      setTimeout(sendTabChangedEvents, 300);
    });

    document.addEventListener("click", function (e) {
      // Send mParticle Navigation event if the Let's Get started button is clicked in desktop.
      const isCtoButton =
        e.target &&
        e.target.tagName === "BUTTON-CONTENT" &&
        e.target.textContent?.toLowerCase().includes("get started");
      if (isCtoButton) setTimeout(sendTabChangedEvents, 300);
    });

    document.addEventListener("submit", function (e) {
      if (e.target?.id !== "rf-email-result") return;

      // Send "Email My Results" Ritual Finder event
      const email = e.target.querySelector("input[type='email']").value;
      const newsletterSubscribed = e.target.querySelector(
        "input#rf-result-email-checkbox"
      )?.checked;

      window.Shopify?.analytics.publish("mp:rf-send-email", {
        email,
        newsletterSubscribed,
      });
    });
  } catch (error) {
    console.warn("[mParticle] Error setting up Ritual Finder events:", error);
  }
}

// ============================================================================
// SUBSCRIBE AND SAVE EVENTS
// ============================================================================

function setupSubscribeAndSaveEvents() {
  try {
    function onOrderGrooveFormSubmissions(event) {
      const ogActionKey = event.target?.dataset.ogActionKey;
      if (!ogActionKey) return;

      // Helper utilities
      const getValue = (selector) =>
        event?.target?.querySelector(selector)?.value;

      const getOgWrapper = () => event?.target?.closest(".og-content-wrapper");

      const getFreq = () =>
        getOgWrapper()
          ?.querySelector("select[name='frequency']")
          ?.value?.toString()
          .trim()
          .split("_")[0];

      const getOrderDetails = (keys, selector) => {
        const wrapperSelector = selector || ".og-order-item";
        const dataset = getOgWrapper()?.querySelector(wrapperSelector)?.dataset;

        if (!dataset) return null;

        const fields = Array.isArray(keys) ? keys : [keys];
        const data = {};
        for (const key of fields) {
          if (dataset[key] !== undefined) {
            data[key] = dataset[key];
          }
        }
        return data;
      };

      const parseFrequency = (value) => value?.toString().trim().split("_")[0];

      const sendMParticleEvent = (payload) => {
        const data = {
          eventType: "Other",
          eventName: "Subscribe & Save Action",
          eventCategory: "Subscribe & Save",
          payload: {
            ...payload,
            pageSource: "Subscribe & Save page",
          },
          options: { removeCustomAttributes: true },
        };

        window.Shopify?.analytics.publish("mp:custom-event", data);
      };

      // Action handlers mapped to their event logic
      const actionHandlers = {
        change_quantity: () => ({
          actionType: "edit quantity",
          frequency: getFreq(),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        change_subscription_frequency: () => ({
          actionType: "frequency update",
          frequency: parseFrequency(getValue('select[name="frequency"]')),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        skip_shipment: () => ({
          actionType: "skip",
          frequency: getFreq(),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        delete_item: () => ({
          actionType: "skip",
          frequency: getFreq(),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        send_now: () => ({
          actionType: "send now",
          frequency: getFreq(),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        pause_subscription_radio: () => ({
          actionType: "pause",
          frequency: getFreq(),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        cancel_subscription: () => {
          const orderData = getOrderDetails([
            "skuid",
            "orderId",
            "subscriptionId",
          ]);
          const cancellationReason = getValue(
            "input[name='cancel_reason']:checked"
          );
          const otherCancellationReason = getValue(
            "input[name='cancel_reason_details']"
          );

          const reason = otherCancellationReason || cancellationReason;
          const cleanedReason = reason?.replace(/^\d+\s*\|\s*/, "");

          return {
            actionType: "cancel subscription",
            frequency: getFreq(),
            productID: orderData?.skuid,
            orderID: orderData?.orderID,
            subscriptionID: orderData?.subscriptionId,
            cancellationReason: cleanedReason || "",
            deviceType: findDeviceType(),
          };
        },

        change_product: () => {
          const swapProductID =
            event.target?.querySelector("input[name='product']:checked")
              ?.dataset?.skuid || "";
          return {
            actionType: "swap",
            frequency: getFreq(),
            productID: getOrderDetails("skuid")?.skuid,
            swapProductID,
            deviceType: findDeviceType(),
          };
        },

        change_shipment_date: () => ({
          actionType: "edit schedule",
          frequency: getFreq(),
          productID: getOrderDetails("skuid")?.skuid,
          deviceType: findDeviceType(),
        }),

        reactivate_subscription: () => ({
          actionType: "reactivate",
          frequency: getFreq(),
          productID: getOrderDetails("skuid", ".og-inactive-product")?.skuid,
          deviceType: findDeviceType(),
        }),
      };

      const handler = actionHandlers[ogActionKey];
      if (handler) {
        const payload = handler();
        if (payload) {
          sendMParticleEvent(payload);
        }
      }
    }

    document.addEventListener("submit", onOrderGrooveFormSubmissions);
  } catch (error) {
    console.warn("[mParticle] Error setting up Subscribe and Save:", error);
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function init() {
  try {
    setupKlaviyoEvents();
    setupPieEyeEvents();
    setupDYEvents();
    setupNavigationEvents();
    setupFilterSelectedEvents();
    setupHueEvents();
    setupMiniBagEvents();
    setupRitualFinderEvents();
    setupSubscribeAndSaveEvents();
    setupProductClickEvents();
  } catch (error) {
    console.warn("[mParticle] Initialization error:", error);
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  // DOM already loaded, initialize immediately
  init();
}
